import type { RatingWithColor } from '@risksmart-app/components/src/hooks/types';
import type { RatingContext } from '@risksmart-app/components/src/hooks/useRating';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useMemo } from 'react';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';

import { useScoringSettings } from './useScoringSettings';

export interface ResolvedRating {
  label: string;
  value: number;
  color?: string;
}

export interface RiskRatingResolver {
  resolveRiskRating: (params: {
    likelihood: number | null | undefined;
    impact: number | null | undefined;
    controlType: Risk_Assessment_Result_Control_Type_Enum;
    rating: number | null | undefined;
    configId?: string | null;
  }) => ResolvedRating | undefined;
  resolveImpact: (
    value: number | null | undefined,
    configId?: string | null
  ) => ResolvedRating | undefined;
  resolveLikelihood: (
    value: number | null | undefined,
    configId?: string | null
  ) => ResolvedRating | undefined;
  hasScoringSettings: boolean;
  maxInherentRating: number;
  maxResidualRating: number;
  options: {
    likelihood: RatingWithColor<number>[];
    impact: RatingWithColor<number>[];
    riskRating: RatingWithColor<number>[];
  };
}

export const useRiskRatingResolver = (
  context: RatingContext = 'standard',
  options: { configType: ConfigType; configIds?: string[] }
): RiskRatingResolver => {
  const {
    hasScoringSettings,
    getRatingByLikelihoodAndImpact,
    getLikelihoodByValue,
    getImpactByValue,
    likelihoodOptions,
    impactOptions,
    riskRatingOptions,
  } = useScoringSettings({
    configType: options.configType,
    configIds: options.configIds,
  });

  const {
    getByValue: getTaxonomyControlledRatingByValue,
    options: taxonomyControlledOptions,
  } = useRating('risk_controlled', context);

  const {
    getByValue: getTaxonomyUncontrolledRatingByValue,
    options: taxonomyUncontrolledOptions,
  } = useRating('risk_uncontrolled', context);

  const {
    getByValue: getTaxonomyLikelihoodByValue,
    options: taxonomyLikelihoodOptions,
  } = useRating('likelihood', context);

  const {
    getByValue: getTaxonomyImpactByValue,
    options: taxonomyImpactOptions,
  } = useRating('impact', context);

  const resolveRiskRating = useCallback(
    ({
      likelihood,
      impact,
      controlType,
      rating,
      configId,
    }: {
      likelihood: number | null | undefined;
      impact: number | null | undefined;
      controlType: Risk_Assessment_Result_Control_Type_Enum;
      rating: number | null | undefined;
      configId?: string | null;
    }): ResolvedRating | undefined => {
      if (hasScoringSettings) {
        if (likelihood != null && impact != null) {
          const cell = getRatingByLikelihoodAndImpact(
            likelihood,
            impact,
            configId
          );
          if (cell) {
            return { label: cell.label, value: cell.value, color: cell.color };
          }
        }

        return undefined;
      }

      const option =
        controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
          ? getTaxonomyControlledRatingByValue(rating)
          : getTaxonomyUncontrolledRatingByValue(rating);

      if (!option || option.value == null) {
        return undefined;
      }

      return {
        label: option.label,
        value: Number(option.value),
        color: 'color' in option ? (option.color as string) : undefined,
      };
    },
    [
      hasScoringSettings,
      getRatingByLikelihoodAndImpact,
      getTaxonomyControlledRatingByValue,
      getTaxonomyUncontrolledRatingByValue,
    ]
  );

  const resolveImpact = useCallback(
    (
      value: number | null | undefined,
      configId?: string | null
    ): ResolvedRating | undefined => {
      if (value == null) {
        return undefined;
      }

      if (hasScoringSettings) {
        const r = getImpactByValue(value, configId);

        return r
          ? { label: r.label, value: r.value, color: r.color }
          : undefined;
      }

      const option = getTaxonomyImpactByValue(value);
      if (!option || option.value == null) {
        return undefined;
      }

      return {
        label: option.label,
        value: Number(option.value),
        color: 'color' in option ? (option.color as string) : undefined,
      };
    },
    [hasScoringSettings, getImpactByValue, getTaxonomyImpactByValue]
  );

  const resolveLikelihood = useCallback(
    (
      value: number | null | undefined,
      configId?: string | null
    ): ResolvedRating | undefined => {
      if (value == null) {
        return undefined;
      }

      if (hasScoringSettings) {
        const r = getLikelihoodByValue(value, configId);

        return r
          ? { label: r.label, value: r.value, color: r.color }
          : undefined;
      }

      const option = getTaxonomyLikelihoodByValue(value);
      if (!option || option.value == null) {
        return undefined;
      }

      return {
        label: option.label,
        value: Number(option.value),
        color: 'color' in option ? (option.color as string) : undefined,
      };
    },
    [hasScoringSettings, getLikelihoodByValue, getTaxonomyLikelihoodByValue]
  );

  const resolverOptions = useMemo(
    () => ({
      likelihood: hasScoringSettings
        ? likelihoodOptions
        : taxonomyLikelihoodOptions.map((option) => ({
            label: option.label,
            value: Number(option.value),
            color: option.color ?? '',
          })),
      impact: hasScoringSettings
        ? impactOptions
        : taxonomyImpactOptions.map((option) => ({
            label: option.label,
            value: Number(option.value),
            color: option.color ?? '',
          })),
      riskRating: riskRatingOptions,
    }),
    [
      hasScoringSettings,
      likelihoodOptions,
      impactOptions,
      taxonomyLikelihoodOptions,
      taxonomyImpactOptions,
      riskRatingOptions,
    ]
  );

  const maxInherentRating = useMemo(() => {
    if (hasScoringSettings) {
      return Math.max(...riskRatingOptions.map((o) => o.value), 0);
    }

    return Math.max(
      ...taxonomyUncontrolledOptions.map((o) => Number(o.value) || 0)
    );
  }, [hasScoringSettings, riskRatingOptions, taxonomyUncontrolledOptions]);

  const maxResidualRating = useMemo(() => {
    if (hasScoringSettings) {
      return Math.max(...riskRatingOptions.map((o) => o.value), 0);
    }

    return Math.max(
      ...taxonomyControlledOptions.map((o) => Number(o.value) || 0)
    );
  }, [hasScoringSettings, riskRatingOptions, taxonomyControlledOptions]);

  return useMemo(
    () => ({
      resolveRiskRating,
      resolveImpact,
      resolveLikelihood,
      hasScoringSettings,
      maxInherentRating,
      maxResidualRating,
      options: resolverOptions,
    }),
    [
      resolveRiskRating,
      resolveImpact,
      resolveLikelihood,
      hasScoringSettings,
      maxInherentRating,
      maxResidualRating,
      resolverOptions,
    ]
  );
};
